// pages/FeedbackPage.jsx
import React, { useState } from 'react';
import Layout from '../components/common/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import useAuth from '../hooks/useAuth';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

const FeedbackPage = () => {
  const { user } = useAuth();
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    { value: 'login', label: 'Giriş Yapma Sorunu', icon: '🔐' },
    { value: 'register', label: 'Kayıt Olma Sorunu', icon: '📝' },
    { value: 'reservation', label: 'Rezervasyon Sorunu', icon: '📅' },
    { value: 'payment', label: 'Ödeme / Dekont Sorunu', icon: '💳' },
    { value: 'pin', label: 'Kapı Kodu Sorunu', icon: '🔑' },
    { value: 'ui', label: 'Arayüz / Görüntü Sorunu', icon: '🖥️' },
    { value: 'suggestion', label: 'Öneri', icon: '💡' },
    { value: 'other', label: 'Diğer', icon: '❓' },
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + screenshots.length > 5) {
      setError('En fazla 5 görsel ekleyebilirsiniz.');
      return;
    }

    // Dosya boyutu kontrolü (max 5MB per file)
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError('Dosya boyutu 5MB\'dan büyük olamaz.');
        return false;
      }
      return true;
    });

    setScreenshots(prev => [...prev, ...validFiles]);
    
    // Preview URLs oluştur
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      setError('Lütfen bir kategori seçin.');
      return;
    }
    
    if (!description.trim()) {
      setError('Lütfen sorununuzu açıklayın.');
      return;
    }

    if (description.trim().length < 20) {
      setError('Açıklama en az 20 karakter olmalıdır.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Screenshotları yükle
      const screenshotUrls = [];
      for (const file of screenshots) {
        const fileName = `feedback/${user.uid}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        screenshotUrls.push(url);
      }

      // Feedback'i Firestore'a kaydet
      await addDoc(collection(db, 'feedback'), {
        userId: user.uid,
        userEmail: user.email,
        category,
        categoryLabel: categories.find(c => c.value === category)?.label || category,
        description: description.trim(),
        screenshots: screenshotUrls,
        status: 'new', // new, in-progress, resolved, closed
        createdAt: Timestamp.now(),
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
      });

      setSuccess(true);
      setCategory('');
      setDescription('');
      setScreenshots([]);
      setPreviewUrls([]);
    } catch (err) {
      console.error('Feedback gönderme hatası:', err);
      setError('Feedback gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <Card className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-green-400 mb-4">
              Geri Bildiriminiz Alındı!
            </h2>
            <p className="text-gray-300 mb-6">
              Sorununuz en kısa sürede incelenecek ve gerekirse sizinle iletişime geçilecektir.
              Geri bildiriminiz için teşekkür ederiz.
            </p>
            <div className="flex justify-center gap-4">
              <Button onClick={() => setSuccess(false)} variant="secondary">
                Yeni Feedback Gönder
              </Button>
              <Button onClick={() => window.history.back()} variant="primary">
                Geri Dön
              </Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center">
            <svg className="w-8 h-8 mr-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Geri Bildirim
          </h1>
          <p className="text-gray-400 mt-2">
            Yaşadığınız sorunları veya önerilerinizi bizimle paylaşın.
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Kategori Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Sorun Kategorisi *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      category === cat.value
                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                        : 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{cat.icon}</div>
                    <div className="text-xs">{cat.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Açıklama */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Sorununuzu Açıklayın *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Yaşadığınız sorunu detaylı bir şekilde açıklayın. Ne yapmaya çalışıyordunuz? Ne oldu? Ne bekliyordunuz?"
                rows={6}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {description.length}/1000 karakter (minimum 20)
              </p>
            </div>

            {/* Screenshot Yükleme */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ekran Görüntüsü (Opsiyonel)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Sorununuzu daha iyi anlamamız için ekran görüntüsü ekleyebilirsiniz (max 5 adet, her biri max 5MB)
              </p>
              
              {/* Preview */}
              {previewUrls.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-4">
                  {previewUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Screenshot ${index + 1}`}
                        className="w-24 h-24 object-cover rounded-lg border border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={() => removeScreenshot(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full text-white text-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {screenshots.length < 5 && (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer hover:border-gray-500 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-400">
                      Görsel eklemek için tıklayın
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Gönder Butonu */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Gönderiliyor...
                </span>
              ) : (
                'Geri Bildirim Gönder'
              )}
            </Button>
          </form>
        </Card>

        {/* SSS */}
        <Card className="mt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Sık Karşılaşılan Sorunlar</h3>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <p className="font-medium text-blue-300">🔐 Giriş yapamıyorum</p>
              <p className="text-gray-400 mt-1">Email doğrulaması yaptığınızdan emin olun. Spam klasörünü kontrol edin.</p>
            </div>
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <p className="font-medium text-blue-300">🔑 Kapı kodu çalışmıyor</p>
              <p className="text-gray-400 mt-1">Kod sadece rezervasyon saatinizde aktiftir. Saati kontrol edin.</p>
            </div>
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <p className="font-medium text-blue-300">📅 Rezervasyon yapamıyorum</p>
              <p className="text-gray-400 mt-1">Dekont yüklemeniz ve onaylanması gerekebilir. Admin onayını bekleyin.</p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default FeedbackPage;
